import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock, MapPin, User, Users, CheckCircle2, Loader2, Mail, Phone, Hash, Rocket, Info } from 'lucide-react';
import { Sec, W, FormField, Toast, formatTime } from '../components/Shared';
import { POSTER_THEMES } from '../data/websiteData';

const EventDetailPage = ({ events }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const event = events.find(e => e.slug === slug);
  const i = event ? events.indexOf(event) : 0;

  const [tab, setTab] = useState('register');
  const [rsvp, setRsvp] = useState({ name: '', email: '' });
  const [reg, setReg] = useState({ name: '', email: '', phone: '', roll: '' });
  const [rsvpErrors, setRsvpErrors] = useState({});
  const [regErrors, setRegErrors] = useState({});
  const [rsvpStatus, setRsvpStatus] = useState('idle');
  const [regStatus, setRegStatus] = useState('idle');
  const [toast, setToast] = useState(null);


  if (!event) return <div style={{ padding: '200px 0', textAlign: 'center' }}>Event not found. <Link to="/events">Go back</Link></div>;

  const pct = Math.round((event.registered / event.seats) * 100);
  const theme = POSTER_THEMES[i % 6];

  const showToast = (message, type) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToast({ id, message, type });
  };

  const handleRsvpChange = (id, val) => { setRsvp(p => ({ ...p, [id]: val })); setRsvpErrors(p => ({ ...p, [id]: '' })); };
  const handleRegChange = (id, val) => { setReg(p => ({ ...p, [id]: val })); setRegErrors(p => ({ ...p, [id]: '' })); };

  const submitRsvp = async e => {
    e.preventDefault();
    const errs = {};
    if (!rsvp.name.trim()) errs.name = 'Required';
    if (!rsvp.email.trim() || !/\S+@\S+\.\S+/.test(rsvp.email)) errs.email = 'Valid email required';
    if (Object.keys(errs).length) { setRsvpErrors(errs); return; }
    setRsvpStatus('loading');
    try {
      const res = await fetch('/api/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'RSVP',
          eventId: event.id,
          eventTitle: event.title,
          name: rsvp.name,
          email: rsvp.email,
          timestamp: new Date().toISOString(),
          Timestamp: new Date().toISOString(),
          Name: rsvp.name,
          Email: rsvp.email,
          EventTitle: event.title
        })
      });
      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      if (data.status === 'error') throw new Error(data.message || 'API Error');

      setRsvpStatus('success');
      showToast(`RSVP confirmed for "${event.title}"!`, 'success');
      setRsvp({ name: '', email: '' });
    } catch { showToast('RSVP failed. Please try again.', 'error'); setRsvpStatus('idle'); }
  };

  const submitReg = async e => {
    e.preventDefault();
    const errs = {};
    if (!reg.name.trim()) errs.name = 'Required';
    if (!reg.email.trim() || !/\S+@\S+\.\S+/.test(reg.email)) errs.email = 'Valid email required';
    if (!reg.phone.trim()) errs.phone = 'Required';
    if (!reg.roll.trim()) errs.roll = 'Required';
    if (Object.keys(errs).length) { setRegErrors(errs); return; }
    setRegStatus('loading');
    try {
      const res = await fetch('/api/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'REGISTER',
          eventId: event.id,
          eventTitle: event.title,
          name: reg.name,
          email: reg.email,
          phone: reg.phone,
          roll: reg.roll,
          timestamp: new Date().toISOString(),
          Timestamp: new Date().toISOString(),
          Name: reg.name,
          Email: reg.email,
          Phone: Number(reg.phone) || reg.phone,
          Roll: Number(reg.roll) || reg.roll,
          EventTitle: event.title
        })
      });
      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      if (data.status === 'error') throw new Error(data.message || 'API Error');

      setRegStatus('success');
      showToast(`Registered for "${event.title}"!`, 'success');
      setReg({ name: '', email: '', phone: '', roll: '' });
    } catch { showToast('Registration failed. Please try again.', 'error'); setRegStatus('idle'); }
  };

  const SuccessView = ({ title, sub, onReset }) => (
    <div style={{ textAlign: 'center', padding: '40px 0' }} className="af">
      <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', animation: 'checkPop .5s ease' }}>
        <CheckCircle2 size={32} style={{ color: 'var(--green)' }} />
      </div>
      <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--slate-900)', marginBottom: 12 }}>{title}</h4>
      <p style={{ fontSize: 15, color: 'var(--slate-500)', marginBottom: 24 }}>{sub}</p>
      <button className="btn btn-outline" onClick={onReset}>Submit Another</button>
    </div>
  );

  return (
    <div className="af">
      {/* HEADER BANNER */}
      <div style={{ position: 'relative', background: theme.bg, paddingTop: 160, paddingBottom: 100, overflow: 'hidden' }}>
        {event.bannerImg && <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}><img src={event.bannerImg} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }} /></div>}
        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(circle at 20% 20%, ${theme.dot} 0%, transparent 40%)`, zIndex: 1 }} />
        <div style={{ position: 'relative', zIndex: 2 }}>
          <W>
            <button onClick={() => navigate('/events')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#fff', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 32, opacity: .7 }} onMouseOver={e => e.currentTarget.style.opacity = 1} onMouseOut={e => e.currentTarget.style.opacity = .7}>
              <ArrowLeft size={16} /> Back to Timeline
            </button>
            <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
              <span className={`badge ${event.bc}`} style={{ background: '#fff' }}>{event.type}</span>
              <span className="badge" style={{ background: 'rgba(255,255,255,.15)', color: '#fff' }}>{event.status}</span>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 24, maxWidth: 800 }}>{event.title}</h1>
            <div style={{ height: 4, width: 64, background: theme.bar, borderRadius: 2 }} />
          </W>
        </div>
      </div>

      <Sec py={80}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 420px', gap: 64 }} className="g2">
          {/* EVENT DETAILS */}
          <div>
            <div className="ev-detail-grid" style={{ marginBottom: 48, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {[[<Clock size={16} />, 'Time', formatTime(event.time)], [<MapPin size={16} />, 'Location', event.loc], [<User size={16} />, 'Speaker', event.speaker], [<Users size={16} />, 'Seats', `${event.seats} total capacity`]].map(([ico, l, v]) => (
                <div key={l} style={{ background: 'var(--surface-container-low)', borderRadius: 12, padding: '16px 20px', display: 'flex', gap: 12 }}>
                  <span style={{ color: 'var(--primary)', flexShrink: 0, background: '#fff', width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' }}>{ico}</span>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--slate-500)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>{l}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--slate-800)' }}>{v}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: 48 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 16 }}>About this {event.type}</h3>
              <p style={{ fontSize: 16, color: 'var(--on-surface-variant)', lineHeight: 1.7 }}>{event.desc}</p>
            </div>

            {event.agenda && Array.isArray(event.agenda) && event.agenda.length > 0 && (
              <div style={{ marginBottom: 48 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 24 }}>Event Agenda</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {event.agenda.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', fontSize: 15, color: 'var(--on-surface-variant)', padding: '16px 20px', background: 'var(--surface-container-low)', borderRadius: 12 }}>
                      <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                      <span style={{ marginTop: 1 }}>{a}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {event.tags && Array.isArray(event.tags) && event.tags.length > 0 && (
              <div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 16 }}>Tags</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {event.tags.map((t, j) => <span key={`${t}-${j}`} style={{ fontSize: 13, fontWeight: 600, background: 'var(--surface-container-highest)', color: 'var(--on-surface-variant)', padding: '6px 14px', borderRadius: 100 }}>{t}</span>)}
                </div>
              </div>
            )}
          </div>

          {/* REGISTRATION SIDEBAR */}
          <aside>
            <div className="card" style={{ position: 'sticky', top: 100 }}>
              <div style={{ padding: '32px 32px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--slate-500)', marginBottom: 8, fontWeight: 600 }}>
                  <span>Capacity Filled</span>
                  <span style={{ color: pct > 80 ? 'var(--red)' : 'var(--primary)', fontWeight: 700 }}>{event.registered}/{event.seats} ({pct}%)</span>
                </div>
                <div className="prog-track" style={{ height: 8, marginBottom: 24 }}><div className="prog-fill" style={{ width: `${pct}%`, background: pct > 80 ? 'linear-gradient(90deg,var(--amber),var(--red))' : undefined }} /></div>

                {!(event.status && event.status.trim().toLowerCase() === 'completed') && (
                  <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
                    {[['register', 'Register Now'], ['rsvp', 'RSVP (Free)']].map(([t, l]) => (
                      <button key={t} onClick={() => setTab(t)} style={{ padding: '12px 16px', flex: 1, fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', border: 'none', background: 'none', color: tab === t ? 'var(--primary)' : 'var(--slate-400)', borderBottom: `2px solid ${tab === t ? 'var(--primary)' : 'transparent'}`, marginBottom: -1, transition: 'all .2s' }}>{l}</button>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ padding: '0 32px 32px' }}>
                {event.status && event.status.trim().toLowerCase() === 'completed' ? (
                  <div className="af" style={{ textAlign: 'center', color: 'var(--slate-500)', paddingTop: 16 }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--surface-container-high)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                      <CheckCircle2 size={24} style={{ color: 'var(--slate-400)' }} />
                    </div>
                    <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 8 }}>Event Concluded</h4>
                    <p style={{ fontSize: 14 }}>This event has already taken place. Thank you to everyone who participated!</p>
                  </div>
                ) : (
                  <>
                    {tab === 'register' && (
                      <div className="af">
                        <div style={{ marginBottom: 24, padding: 12, background: 'var(--green-light)', borderRadius: 8, fontSize: 13, color: 'var(--green)', display: 'flex', gap: 10, lineHeight: 1.5 }}>
                          <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                          Registration secures your seat and provides an official attendance certificate.
                        </div>
                        {regStatus === 'success' ? <SuccessView title="Registered Successfully!" sub="Bring your student ID to the event." onReset={() => setRegStatus('idle')} /> : (
                          <form onSubmit={submitReg} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <FormField id="name" label="Full Name" placeholder="Your full name" value={reg.name} onChange={handleRegChange} error={regErrors.name} icon={<User size={14} />} />
                            <FormField id="email" label="Institutional Email" placeholder="your@email.com" type="email" value={reg.email} onChange={handleRegChange} error={regErrors.email} icon={<Mail size={14} />} />
                            <FormField id="phone" label="Phone Number" placeholder="+880 1XXX XXXXXX" type="number" value={reg.phone} onChange={handleRegChange} error={regErrors.phone} icon={<Phone size={14} />} />
                            <FormField id="roll" label="Student Roll" placeholder="e.g. 120220503" type="number" value={reg.roll} onChange={handleRegChange} error={regErrors.roll} icon={<Hash size={14} />} />
                            <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={regStatus === 'loading'}>
                              {regStatus === 'loading' ? <><Loader2 size={16} className="spin" /> Processing…</> : <><Rocket size={16} /> Confirm Registration</>}
                            </button>
                          </form>
                        )}
                      </div>
                    )}

                    {tab === 'rsvp' && (
                      <div className="af">
                        <div style={{ marginBottom: 24, padding: 12, background: 'rgba(59,130,246,.08)', borderRadius: 8, fontSize: 13, color: 'var(--primary)', display: 'flex', gap: 10, lineHeight: 1.5 }}>
                          <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                          RSVP helps us estimate attendance. For guaranteed seating and certificates, use Registration.
                        </div>
                        {rsvpStatus === 'success' ? <SuccessView title="RSVP Confirmed!" sub="We'll remind you 24 hours before." onReset={() => setRsvpStatus('idle')} /> : (
                          <form onSubmit={submitRsvp} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <FormField id="name" label="Full Name" placeholder="Your name" value={rsvp.name} onChange={handleRsvpChange} error={rsvpErrors.name} icon={<User size={14} />} />
                            <FormField id="email" label="Email Address" placeholder="your@email.com" type="email" value={rsvp.email} onChange={handleRsvpChange} error={rsvpErrors.email} icon={<Mail size={14} />} />
                            <button className="btn btn-outline btn-lg" type="submit" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={rsvpStatus === 'loading'}>
                              {rsvpStatus === 'loading' ? <><Loader2 size={16} className="spin" /> Submitting…</> : 'Confirm RSVP'}
                            </button>
                          </form>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>
      </Sec>
      {toast && <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default EventDetailPage;
